import React from 'react';
import Form from 'react-bootstrap/Form';
import Select, { MultiValue, StylesConfig } from 'react-select';

interface OpcaoSelect {
  value: string;
  label: string;
}

interface BarraDeFiltrosProps {
  busca: string;
  onBuscaChange: (v: string) => void;
  ufsDisponiveis: string[];
  ufsSelecionadas: string[];
  onUfsChange: (v: string[]) => void;
  subsistemasSmDisponiveis: string[];
  subsistemasSmSelecionados: string[];
  onSubsistemasSmChange: (v: string[]) => void;
  subsistemasShDisponiveis: string[];
  subsistemasShSelecionados: string[];
  onSubsistemasShChange: (v: string[]) => void;
  localizacoesEpeDisponiveis: string[];
  localizacoesEpeSelecionadas: string[];
  onLocalizacoesEpeChange: (v: string[]) => void;
  // FE-LAYOUT-007 #6: filtros Sub. SH/SM viram context-aware.
  // 'sh' -> mostra apenas Sub. SH; 'sm' (Semanal/Comparativo) -> mostra
  // apenas Sub. SM. dim_windx_conjuntos tem duas FKs distintas
  // (id_subsistema_sh/id_subsistema_sm — ADR-019), cada tela so consulta
  // a sua; expor a outra simultaneamente sugere efeito que nao existe.
  tela: 'sh' | 'sm';
}

// Mesma config visual do SeletorUsinasEolicas — alinha altura ao input
// search do Bootstrap.
//
// FE-LAYOUT-007 #5a: neutralizar o "x" vermelho do multiValueRemove
// (default do react-select em hover). Vermelho permanente = falso alarme
// para acao passiva (remover filtro). Cor inherit + sutil escurecimento
// em hover; padrao Material/Carbon/AntD.
const estilosSelect: StylesConfig<OpcaoSelect, true> = {
  control: (base) => ({
    ...base,
    minHeight: '38px',
    fontSize: '0.875rem',
  }),
  menu: (base) => ({ ...base, fontSize: '0.875rem', zIndex: 1060 }),
  multiValue: (base) => ({ ...base, fontSize: '0.8125rem' }),
  multiValueRemove: (base) => ({
    ...base,
    color: 'inherit',
    opacity: 0.6,
    ':hover': {
      backgroundColor: 'transparent',
      color: 'inherit',
      opacity: 1,
    },
  }),
};

function paraOpcoes(valores: string[]): OpcaoSelect[] {
  return valores.map((v) => ({ value: v, label: v }));
}

/**
 * Barra superior do modal SeletorConjuntos. Tem 4 multi-selects
 * (UF, Subsistema SM, Subsistema SH, Localizacao EPE) — um a mais que
 * o SeletorUsinasEolicas, refletindo o shape mais rico de conjunto.
 */
export default function BarraDeFiltros({
  busca,
  onBuscaChange,
  ufsDisponiveis,
  ufsSelecionadas,
  onUfsChange,
  subsistemasSmDisponiveis,
  subsistemasSmSelecionados,
  onSubsistemasSmChange,
  subsistemasShDisponiveis,
  subsistemasShSelecionados,
  onSubsistemasShChange,
  localizacoesEpeDisponiveis,
  localizacoesEpeSelecionadas,
  onLocalizacoesEpeChange,
  tela,
}: BarraDeFiltrosProps) {
  const handleMulti = (
    onChange: (v: string[]) => void,
  ) => (sel: MultiValue<OpcaoSelect>) => onChange(sel.map((o) => o.value));

  return (
    <div className="windx-seletor-conjuntos__filtros">
      <Form.Group className="windx-seletor-conjuntos__filtros-busca">
        <Form.Label className="visually-hidden" htmlFor="windx-conjuntos-busca">
          Buscar conjuntos
        </Form.Label>
        <Form.Control
          id="windx-conjuntos-busca"
          type="search"
          placeholder="Buscar nome ou código..."
          value={busca}
          onChange={(e) => onBuscaChange(e.target.value)}
          autoFocus
        />
      </Form.Group>
      <div className="windx-seletor-conjuntos__filtros-select">
        <Select<OpcaoSelect, true>
          isMulti
          inputId="windx-conjuntos-uf"
          placeholder="UF · todas"
          options={paraOpcoes(ufsDisponiveis)}
          value={paraOpcoes(ufsSelecionadas)}
          onChange={handleMulti(onUfsChange)}
          styles={estilosSelect}
          noOptionsMessage={() => 'Nenhuma UF'}
          aria-label="Filtrar por UF"
        />
      </div>
      {tela === 'sm' && (
        <div className="windx-seletor-conjuntos__filtros-select">
          <Select<OpcaoSelect, true>
            isMulti
            inputId="windx-conjuntos-sub-sm"
            placeholder="Sub. SM · todos"
            options={paraOpcoes(subsistemasSmDisponiveis)}
            value={paraOpcoes(subsistemasSmSelecionados)}
            onChange={handleMulti(onSubsistemasSmChange)}
            styles={estilosSelect}
            noOptionsMessage={() => 'Nenhum subsistema'}
            aria-label="Filtrar por subsistema SM"
          />
        </div>
      )}
      {tela === 'sh' && (
        <div className="windx-seletor-conjuntos__filtros-select">
          <Select<OpcaoSelect, true>
            isMulti
            inputId="windx-conjuntos-sub-sh"
            placeholder="Sub. SH · todos"
            options={paraOpcoes(subsistemasShDisponiveis)}
            value={paraOpcoes(subsistemasShSelecionados)}
            onChange={handleMulti(onSubsistemasShChange)}
            styles={estilosSelect}
            noOptionsMessage={() => 'Nenhum subsistema'}
            aria-label="Filtrar por subsistema SH"
          />
        </div>
      )}
      <div className="windx-seletor-conjuntos__filtros-select">
        <Select<OpcaoSelect, true>
          isMulti
          inputId="windx-conjuntos-epe"
          placeholder="Loc. EPE · todas"
          options={paraOpcoes(localizacoesEpeDisponiveis)}
          value={paraOpcoes(localizacoesEpeSelecionadas)}
          onChange={handleMulti(onLocalizacoesEpeChange)}
          styles={estilosSelect}
          noOptionsMessage={() => 'Nenhuma localização'}
          aria-label="Filtrar por localização EPE"
        />
      </div>
    </div>
  );
}
